import React, {useState, useEffect, useRef} from 'react';
import NoteService from '../../../services/NoteService';
import { MdOutlineExpandMore, MdOutlineExpandLess, MdPlayArrow, MdPause, MdSkipNext, MdQueueMusic, MdSkipPrevious} from "react-icons/md";
import parse from 'html-react-parser';
import useIsMobile from "../../../utils/useIsMobile";
import Dictaphone1 from "../../../utils/Assist";
import Spinner2 from "../../../UI/Spinner/Spinner2";
import VideoPlayer from "../VideoPlayer/VideoPlayer";
import AudioPlayer from "./AudioControls";

const Accordion = ({ title, parameter, onExpandClick, id, number, videoNames }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [contentWithImages, setContentWithImages] = useState([]);
    const [audioFiles, setAudioFiles] = useState([]);
    const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPlayingAll, setIsPlayingAll] = useState(false);
    const [dataFetched, setDataFetched] = useState(false);
    const audioRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const accordionRef = useRef(null);
    const [videos, setVideos] = useState([]);

    const isMobile = useIsMobile();

    const toggleAccordion = async () => {
        if (!isOpen && !dataFetched) {
            setLoading(true);
            try {
                const response = await NoteService.getDescription(parameter, number);
                const { text, images, videos: videoUrls, audio } = response;
                setVideos(videoUrls || []);
                const paragraphs = text ? text.split('\n') : [];
                const contentWithImages = paragraphs.map((paragraph, index) => ({
                    paragraph: processText(paragraph, images, videoUrls),
                    paragraphNumber: index + 1,
                }));
                setContentWithImages(contentWithImages);
                setAudioFiles(Array.isArray(audio) ? audio : []);
                setDataFetched(true);
            } catch (error) {
                console.error('Error fetching accordion data:', error);
            } finally {
                setLoading(false);
            }
        }
        // Open or close the accordion without redundant calls
        setIsOpen(!isOpen);
        if (!isOpen && accordionRef.current) {
            accordionRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    };



    const parseContent = (content, videoUrls) => {
        const parts = content.split(/<VIDEO_MARKER>(.*?)<\/VIDEO_MARKER>/);
        const videoRegex = /\[cat([A-I]\d+)_\d+_vid\]/; // Corrected regex

        return parts.map((part, index) => {
            const trimmedPart = part.trim();
            const videoMatch = trimmedPart.match(videoRegex);
            if (videoMatch) {
                const fullMatch = videoMatch[0];
                const videoIndex = videoUrls.findIndex((url) => url.includes(fullMatch.replace(/[\[\]]/g, '').replace('_vid', '')));
                if (videoIndex !== -1) {
                    return <VideoPlayer key={`video-${index}`} videoUrl={videoUrls[videoIndex]}/>;
                } else {
                    const parsedContent = parse(part);
                    if (Array.isArray(parsedContent) && parsedContent.length > 0) {
                        return parsedContent.map((item, i) => (
                            React.isValidElement(item) ?
                                React.cloneElement(item, { key: `parsed-item-${index}-${i}` }) :
                                <React.Fragment key={`fragment-${index}-${i}`}>{item}</React.Fragment>
                        ));
                    } else if (React.isValidElement(parsedContent)) {
                        return React.cloneElement(parsedContent, { key: `parsed-single-${index}` });
                    } else if (typeof parsedContent === 'string' || parsedContent instanceof String) {
                        return <React.Fragment key={`string-${index}`}>{parsedContent}</React.Fragment>;
                    } else {
                        return null;
                    }
                }
            } else if (trimmedPart) { // Only parse non-empty text parts
                const parsedContent = parse(trimmedPart);
                if (Array.isArray(parsedContent) && parsedContent.length > 0) {
                    return parsedContent.map((item, i) => (
                        React.isValidElement(item) ?
                            React.cloneElement(item, { key: `parsed-item-${index}-${i}` }) :
                            <React.Fragment key={`fragment-${index}-${i}`}>{item}</React.Fragment>
                    ));
                } else if (React.isValidElement(parsedContent)) {
                    return React.cloneElement(parsedContent, { key: `parsed-single-${index}` });
                } else if (typeof parsedContent === 'string' || parsedContent instanceof String) {
                    return <React.Fragment key={`string-${index}`}>{parsedContent}</React.Fragment>;
                } else {
                    return null;
                }
            }
            return null;
        });
    };

    const processText = (text, imageUrls, videoUrls) => {
        const stegRegex = /steg\s(1[0-9]|20|[1-9])/gi;
        const fallRegex = /fall\s(1[0-9]|20|[1-9])/gi;
        const automatRegex = /-AUTOMAT-/gi;
        const manuellRegex = /-MANUELL-/gi;
        const numberDotRegex = /(?:^|\s)(1[0-9]|20|[1-9])\./g;
        const redRegex = /\*\*RED\*\*([^\*]+)\*\*/g;
        const boldRegex = /\*\*BOLD\*\*([^\*]+)\*\*/g;
        const greenRegex = /\*\*GREEN\*\*([^\*]+)\*\*/g;
        const infoIconPlaceholder = /__INFO_ICON__/g;  // Use this as a placeholder
        const names = /\[cat([A-I])([1-9]|[1-9][0-9]|1[0-9]{2}|200)_(1[0-9]|20|[1-9])_vid\]/g; // Use this as a placeholder
        const breakReg = /\*\*BREAK\*\*/g;  // Updated regex to match **BREAK** without capturing additional text

        // Start processing the text and store it in processedText
        let processedText = text
            .replace(stegRegex, match => {
                const className = isMobile ? "highlightedM" : "highlighted";
                return `<strong className="${className}">${match}</strong>`;
            })
            .replace(fallRegex, match => {
                const className = isMobile ? "highlightedM" : "highlighted";
                return `<strong className="${className}">${match}</strong>`;
            })
            .replace(automatRegex, match => {
                const className = isMobile ? "highlighted2MB" : "highlighted";
                return `<strong className="${className}">${match}</strong>`;
            })
            .replace(manuellRegex, match => {
                const className = isMobile ? "highlighted2MB" : "highlighted";
                return `<strong className="${className}">${match}</strong>`;
            })
            .replace(numberDotRegex, match => {
                const className = isMobile ? "highlightedM" : "highlighted";
                return `<strong className="${className}">${match}</strong>`;
            })
            .replace(redRegex, (match, redText) => `<strong className="text-red">${redText}</strong>`)
            .replace(boldRegex, (match, boldText) => {
                const className = isMobile ? "highlightedM pb-8 pt-8" : "highlighted pb-16 pt-16";
                return `<strong className="${className}">${boldText}</strong>`;
            })
            .replace(greenRegex, (match, greenText) => `<strong className="text-green">${greenText}</strong>`)
            .replace(breakReg, (match, breakText) => `<p style="padding-bottom: 5px;"></p>`)
            .replace(infoIconPlaceholder, match => {
                const className = isMobile ? "info-iconM" : "info-icon";
                return `<strong className="${className}">I</strong>`;
            })




            .replace(/\[cat([A-I])([1-9]|[1-9][0-9]|1[0-9]{2}|200)_(1[0-9]|20|[1-9])\]/g, (match, catGroup, number, index) => {
                const imageIndex = parseInt(index, 10) - 1;
                const imageUrlsForIndex = imageUrls.filter((_, i) => i === imageIndex);

                if (!imageUrlsForIndex.length) {
                    return match;
                }

                return `
                  <span style="display: flex; justify-content: center; padding: 8px 0;">
        ${imageUrlsForIndex
                    .map((imageUrl, innerIndex) => {
                        const style = isMobile
                            ? 'max-width:99%; height:80%; border-radius:8px; margin:4px;'
                            : 'max-width:70%; height:60%; border-radius:8px; margin:4px;';

                        return `
                    <img
                        class="image"
                        src="${imageUrl}"
                        alt="Step image"
                        style="${style}"
                    />
                `;
                    })
                    .join("")}
    </span>
            `;
            });

        return processedText;
    };

    const pauseAudio = () => { console.log('pause activated pressed')
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    };

    const stopAllAudio = () => {
        pauseAudio();
        setIsPlayingAll(false);
        setCurrentAudioIndex(0);
    };

    useEffect(() => {
        if (isPlayingAll) {
            if (currentAudioIndex < audioFiles.length && audioRef.current) {
                audioRef.current.src = audioFiles[currentAudioIndex];
                audioRef.current.play().catch((error) => console.error("Error playing audio:", error));
                setIsPlaying(true);
            } else {
                stopAllAudio();
            }
        }
    }, [currentAudioIndex, isPlayingAll]);

    const handleAudioEnded = () => {
        if (isPlayingAll) {
            setCurrentAudioIndex((prevIndex) => prevIndex + 1);
        } else {
            setIsPlaying(false);
        }};

    return (
        <>
            <div ref={accordionRef}>
                {isMobile ? (
                    <div className="accordion-m left">
                        <div
                            className="accordion-header center"
                            style={{ backgroundColor: '#f1ebff', padding: '10px', marginBottom: '-5px', borderRadius: '5px' }}
                            onClick={toggleAccordion}>
                            <span>{title}</span>
                            {isOpen ? <MdOutlineExpandLess className="custom-icon" /> : <MdOutlineExpandMore className="custom-icon" />}
                        </div>
                        {isOpen && (
                            <div style={{ backgroundColor: '#f1ebff', padding: '9px', borderRadius: '5px' }}>
                                {loading ? (
                                    <div style={{ textAlign: 'left', padding: '20px' }}>
                                        <Spinner2 />
                                    </div>
                                ) : (
                                    <>
                                        {contentWithImages.map(({ paragraph, paragraphNumber }) => (
                                            <div key={paragraphNumber}>
                                                <section style={{ textAlign: 'left' }}>
                                                    {parseContent(paragraph, videos)} {/* Call parseContent here */}
                                                </section>
                                            </div>
                                        ))}
                                        <span className='center2 jcm jcc'>
                                    </span>
                                        {audioFiles.length > 0 && (
                                            <>
                                                <div className="audio-controls" style={{ textAlign: 'center', backgroundColor: '#f8f4ff', borderRadius: '5px' }}>
                                                    <Dictaphone1
                                                        isAccordionOpen={isOpen}
                                                        id={id}
                                                        parameter={parameter}
                                                        audioFiles={audioFiles}
                                                        audioRef={audioRef}
                                                        onAudioEnded={handleAudioEnded}
                                                    />
                                                    <audio
                                                        ref={audioRef}
                                                        onEnded={handleAudioEnded}
                                                    />
                                                </div>
                                                <div className="audio-controls center" style={{ paddingTop: '15px', marginTop: '7px', backgroundColor: '#f8f4ff', borderRadius: '5px' }}>

                                                    <p style={{fontSize: '14pt', color: '#54319f', padding: '9px'}} > Styra instruktioner med knappar  </p>

                                                    <AudioPlayer
                                                        audioFiles={audioFiles}
                                                        isPlayingAll={isPlayingAll}
                                                        setIsPlayingAll={setIsPlayingAll}
                                                        currentAudioIndex={currentAudioIndex}
                                                        setCurrentAudioIndex={setCurrentAudioIndex}
                                                    />

                                                </div>
                                            </>


                                        )}</>)}
                            </div>)}
                    </div>
                ) : (
                    <div className="accordion">
                        <div
                            className="accordion-header center2"
                            style={{ backgroundColor: '#f1ebff', padding: '10px', marginBottom: '-5px', borderRadius: '5px' }}
                            onClick={toggleAccordion}>
                            <span>{title}</span>
                            {isOpen ? <MdOutlineExpandLess className="custom-icon" /> : <MdOutlineExpandMore className="custom-icon" />}
                        </div>
                        {isOpen && (
                            <div style={{ backgroundColor: '#f1ebff', padding: '9px', borderRadius: '5px' }}>
                                {loading ? (
                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                        <Spinner2 />
                                    </div>
                                ) : (
                                    <>
                                        {contentWithImages.map(({ paragraph, paragraphNumber }) => (
                                            <div key={paragraphNumber}>
                                                <section style={{ textAlign: 'left' }}>
                                                    {parseContent(paragraph, videos)} {/* Call parseContent here */}
                                                </section>
                                            </div>
                                        ))}
                                        {audioFiles.length > 0 && (
                                            <><div className="audio-controls" style={{ textAlign: 'center',  margin: '5px', marginBottom:'15px', backgroundColor: '#f8f4ff', borderRadius: '5px' }}>
                                                <Dictaphone1
                                                    isAccordionOpen={isOpen}
                                                    id={id}
                                                    parameter={parameter}
                                                    audioFiles={audioFiles}
                                                    audioRef={audioRef}
                                                    onAudioEnded={handleAudioEnded}
                                                />
                                                <audio
                                                    ref={audioRef}
                                                    onEnded={handleAudioEnded}
                                                />
                                            </div>
                                                <div className="audio-controls" style={{ textAlign: 'center', padding:'10px', margin: '5px',backgroundColor: '#f8f4ff', borderRadius: '5px' }}>
                                                    <p style={{fontSize: '14pt', color: '#54319f', padding: '9px'}} > `Spela upp instruktioner</p>

                                                    <AudioPlayer
                                                        audioFiles={audioFiles}
                                                        isPlayingAll={isPlayingAll}
                                                        setIsPlayingAll={setIsPlayingAll}
                                                        currentAudioIndex={currentAudioIndex}
                                                        setCurrentAudioIndex={setCurrentAudioIndex}
                                                    />


                                                </div>
                                            </>)}
                                    </>)}
                            </div>)}
                    </div>)}
            </div>

        </>
    );
};
export default Accordion;